"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { Text } from "../information/text";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface PaginationProps extends React.ComponentProps<"nav"> {
  pageNo?: number;
  noOfPage?: number;
  pageSize?: number;
  enablePageSize?: boolean;
  showPageNumber?: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

function Pagination({
  className,
  pageNo = 1,
  noOfPage = 1,
  pageSize = 100,
  enablePageSize = false,
  showPageNumber = true,
  onPageChange,
  onPageSizeChange,
  ...props
}: PaginationProps) {
  const [currentPage, setCurrentPage] = React.useState(pageNo);
  const [currentPageSize, setCurrentPageSize] = React.useState(pageSize);

  const pageArray = React.useMemo(
    () => Array.from({ length: noOfPage }, (_, i) => i + 1),
    [noOfPage]
  );

  const optionPageSize = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "40", value: 40 },
    { label: "80", value: 80 },
    { label: "100", value: 100 },
    { label: "200", value: 200 },
    { label: "400", value: 400 },
    { label: "600", value: 600 },
    { label: "800", value: 800 },
    { label: "1000", value: 1000 },
  ];

  const createArrayWithLabels = (length: number) => {
    const result = [];
    for (let i = 0; i < length; i++) {
      result.push({ label: `Page ${i + 1}`, value: i + 1 });
    }
    return result;
  };

  const options = React.useMemo(
    () => createArrayWithLabels(noOfPage),
    [noOfPage]
  );

  const getNextPage = (p: number) => {
    if (p > 0 && p <= noOfPage) {
      setCurrentPage(p);
      onPageChange?.(p);
    }
  };

  const handlePageSizeChange = (size: number) => {
    setCurrentPageSize(size);
    onPageSizeChange?.(size);
  };

  const pageClasses = (p: number) =>
    cn(
      styleDrivers.pagination.pageNumber,
      currentPage === p
        ? styleDrivers.pagination.pageNumberActive
        : styleDrivers.pagination.pageNumberInactive
    );

  const prevClasses = cn(
    styleDrivers.pagination.prevButton,
    currentPage === 1 && styleDrivers.pagination.prevButtonDisabled
  );

  const nextClasses = cn(
    styleDrivers.pagination.nextButton,
    currentPage === noOfPage && styleDrivers.pagination.nextButtonDisabled
  );

  const renderPageNumbers = () => {
    if (pageArray.length < 15) {
      return (
        <div className="flex space-x-2 items-center overflow-auto invisible-scrollbar">
          <div className="flex space-x-2">
            {pageArray.map((p) => (
              <div
                key={p}
                className={pageClasses(p)}
                onClick={() => getNextPage(p)}
              >
                <Text className="text-sm">{p}</Text>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-between w-full">
        <div className="flex space-x-1.5">
          {pageArray.slice(0, 3).map((p) => (
            <div
              key={p}
              className={pageClasses(p)}
              onClick={() => getNextPage(p)}
            >
                <Text className="text-sm">{p}</Text>
            </div>
          ))}
        </div>
        <div className="flex space-x-2 w-full justify-evenly">
          {currentPage > 4 && currentPage < noOfPage - 3 && (
            <div className="flex space-x-2">
              {pageArray.slice(currentPage - 2, currentPage + 1).map((p) => (
                <div
                  key={p}
                  className={pageClasses(p)}
                  onClick={() => getNextPage(p)}
                >
                   <Text 
                   className={cn(
                     "text-sm",
                     currentPage === p ? "text-primary-foreground" : "text-secondary-600"
                   )}
                 >
                   {p}
                 </Text>
                </div>
              ))}
            </div>
          )}
          {currentPage === 4 && (
            <div className="flex space-x-2">
              {pageArray.slice(3, 6).map((p) => (
                <div
                  key={p}
                  className={pageClasses(p)}
                  onClick={() => getNextPage(p)}
                >
                   <Text 
                   className={cn(
                     "text-sm",
                     currentPage === p ? "text-primary-foreground" : "text-secondary-600"
                   )}
                 >
                   {p}
                 </Text>
                </div>
              ))}
            </div>
          )}
          {currentPage === pageArray.length - 3 && (
            <div className="flex space-x-2">
              {pageArray
                .slice(pageArray.length - 6, pageArray.length - 3)
                .map((p) => (
                  <div
                    key={p}
                    className={pageClasses(p)}
                    onClick={() => getNextPage(p)}
                  >
                     <Text 
                   className={cn(
                     "text-sm",
                     currentPage === p ? "text-primary-foreground" : "text-secondary-600"
                   )}
                 >
                   {p}
                 </Text>
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="flex space-x-1.5">
          {pageArray.slice(pageArray.length - 3, pageArray.length).map((p) => (
            <div
              key={p}
              className={pageClasses(p)}
              onClick={() => getNextPage(p)}
            >
               <Text className="text-sm">{p}</Text>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <nav
      data-slot="pagination"
      role="navigation"
      aria-label="pagination"
      className={cn(styleDrivers.pagination.root, className)}
      {...props}
    >
      <div className="flex w-full space-x-2 items-center">
        <div
          className={prevClasses}
          onClick={() => getNextPage(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
           <Text className="text-sm font-semibold">Prev</Text>
        </div>

        {showPageNumber && (
          <div className="flex w-full">{renderPageNumbers()}</div>
        )}
      </div>

      <div className="flex space-x-3 items-center">
        <div
          className={nextClasses}
          onClick={() => getNextPage(currentPage + 1)}
        >
           <Text className="text-sm font-semibold">Next</Text>
          <ChevronRight className="h-4 w-4" />
        </div>

        {showPageNumber && (
          <div className="w-32">
            <select
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                getNextPage(page);
              }}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {enablePageSize && (
          <div className="w-24">
            <select
              value={currentPageSize}
              onChange={(e) => {
                const size = parseInt(e.target.value);
                handlePageSizeChange(size);
              }}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              {optionPageSize.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </nav>
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex items-center gap-1", className)}
      {...props}
    />
  );
}

function PaginationItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="pagination-item"
      className={cn("list-none", className)}
      {...props}
    />
  );
}

type PaginationLinkProps = React.ComponentProps<"button"> & {
  isActive?: boolean;
};

function PaginationLink({
  className,
  isActive,
  children,
  disabled,
  ...props
}: PaginationLinkProps) {
  return (
    <button
      type="button"
      data-slot="pagination-link"
      disabled={disabled}
      className={cn(
        "inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm transition-colors",
        "border-input bg-background hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50",
        isActive &&
          "bg-primary text-background hover:bg-foreground/90 border-foreground",
        className
      )}
      {...props}
    >
      {typeof children === "string" || typeof children === "number" ? (
        <Text as="p">{children}</Text>
      ) : (
        children
      )}
    </button>
  );
}

function PaginationPrevious(props: Omit<PaginationLinkProps, "children">) {
  return (
    <PaginationLink aria-label="Previous" {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
    </PaginationLink>
  );
}

function PaginationNext(props: Omit<PaginationLinkProps, "children">) {
  return (
    <PaginationLink aria-label="Next" {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn(styleDrivers.pagination.ellipsis, className)}
      {...props}
    >
      …
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
